import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";

export interface SwitchProps extends ThemeOverrideProps {
  /** Size of the switch */
  size?: "sm" | "md" | "lg" | "xl";
  /** Whether the switch is checked */
  checked?: boolean;
  /** Visual state of the switch */
  state?: "default" | "hover" | "disable" | "error";
  /** Whether the switch is disabled */
  disabled?: boolean;
  /** Label text to display next to the switch */
  label?: string;
  /** Position of the label relative to the switch */
  labelPosition?: "left" | "right";
  /**
   * Description content to display below the label. Accepts rich content, so a
   * link or other inline markup can be embedded:
   *
   * ```tsx
   * <Switch
   *   label="Analytics"
   *   description={<>See our <a href="/privacy">privacy policy</a>.</>}
   * />
   * ```
   *
   * Clicks and Enter presses on interactive content inside the description
   * (links, buttons) do NOT toggle the switch — see `isInteractiveTarget`.
   */
  description?: React.ReactNode;
  /** Error label text to display when state is 'error' */
  errorLabel?: string;
  /** Callback when the switch value changes */
  onValueChange?: (value: boolean) => void;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** ID of the element that labels this switch */
  ariaLabelledBy?: string;
  testID?: string;
}

/** Minimal shape shared by the web (DOM) and native press events. */
type SwitchPressEvent = { target?: unknown };

/**
 * True when the event originated inside interactive content rendered in the
 * label/description slots — a link or a button. The switch row is one big
 * press target, so without this guard a link in `description` could never be
 * followed: the click would be swallowed into a toggle.
 *
 * Web only in practice. On native the event carries a node handle rather than
 * a DOM element, so `closest` is absent and the guard falls through to
 * `false`, preserving the existing toggle-on-press behaviour.
 */
const isInteractiveTarget = (event?: SwitchPressEvent): boolean => {
  const target = event?.target as
    | { closest?: (selectors: string) => unknown }
    | undefined
    | null;

  if (!target || typeof target.closest !== "function") return false;

  return Boolean(
    target.closest('a[href], button, [role="link"], [role="button"]')
  );
};

export const Switch: React.FC<SwitchProps> = ({
  size = "md",
  checked = false,
  state = "default",
  disabled = false,
  label,
  labelPosition = "right",
  description,
  errorLabel,
  onValueChange,
  ariaLabel,
  ariaLabelledBy,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const isDisable = state === "disable" || disabled;
  const isError = state === "error";
  const isHover = state === "hover";
  const showErrorLabel = isError && errorLabel;

  const rawId = useId();
  const safeId = rawId.replace(/:/g, "");
  const descriptionId = `switch-desc-${safeId}`;
  const errorId = `switch-error-${safeId}`;

  const ariaDescribedByParts: string[] = [];
  if (description) ariaDescribedByParts.push(descriptionId);
  if (showErrorLabel) ariaDescribedByParts.push(errorId);
  const ariaDescribedBy =
    ariaDescribedByParts.length > 0
      ? ariaDescribedByParts.join(" ")
      : undefined;

  const sizing = theme.sizing.switch(size);

  const switchColors = theme.colors.control.switch;
  const faintColors = theme.colors.control.faint;
  const knobColors = theme.colors.control.knob;
  const textColors = theme.colors.control.text;

  const handlePress = (event?: SwitchPressEvent) => {
    if (isInteractiveTarget(event)) return;
    if (!isDisable && onValueChange) {
      onValueChange(!checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Let Enter activate a focused link inside the description instead of
    // toggling the switch.
    if (isInteractiveTarget(event)) return;
    if (!isDisable && (event.key === " " || event.key === "Enter")) {
      event.preventDefault();
      handlePress();
    }
  };

  const getBgColor = () => {
    if (isDisable) return switchColors.bgDisable;
    if (isHover) return checked ? switchColors.bgHover : faintColors.bgHover;
    return checked ? switchColors.bg : faintColors.bg;
  };

  const getBorderColor = () => {
    if (isDisable) return switchColors.borderDisable;
    if (isHover)
      return checked ? switchColors.borderHover : faintColors.borderHover;
    return checked ? switchColors.border : faintColors.border;
  };

  const getTextColor = () => {
    if (isDisable) return textColors.disable;
    return textColors.primary;
  };

  const getKnobColor = () => {
    if (isDisable) return knobColors.bgDisable;
    if (isHover)
      return checked ? knobColors.bgActiveHover : knobColors.bgInactiveHover;
    return checked ? knobColors.bgActive : knobColors.bgInactive;
  };

  const labelBlock = (label || description || showErrorLabel) && (
    <Box flexDirection="column" gap={sizing.textGap}>
      {label && (
        <Text
          color={getTextColor()}
          fontSize={sizing.fontSize}
          lineHeight={sizing.lineHeight}
          fontWeight="400"
        >
          {label}
        </Text>
      )}
      {description && (
        <Text
          id={descriptionId}
          color={theme.colors.content.tertiary}
          fontSize={sizing.descriptionFontSize}
          lineHeight={sizing.descriptionLineHeight}
        >
          {description}
        </Text>
      )}
      {showErrorLabel && (
        <Text
          id={errorId}
          color={theme.colors.content.alert.primary}
          fontSize={sizing.descriptionFontSize}
          lineHeight={sizing.descriptionLineHeight}
        >
          {errorLabel}
        </Text>
      )}
    </Box>
  );

  const toggle = (
    <Box
      width={sizing.width}
      height={sizing.height}
      backgroundColor={getBgColor()}
      borderColor={getBorderColor()}
      borderWidth={1}
      borderRadius={sizing.frameBorderRadius}
      padding={1}
      flexDirection="row"
      alignItems="center"
      justifyContent={checked ? "flex-end" : "flex-start"}
      hoverStyle={
        !isDisable && !isHover
          ? {
              backgroundColor: checked
                ? switchColors.bgHover
                : faintColors.bgHover,
              borderColor: checked
                ? switchColors.borderHover
                : faintColors.borderHover,
            }
          : undefined
      }
    >
      <Box
        width={sizing.knobSize}
        height={sizing.knobSize}
        backgroundColor={getKnobColor()}
        borderRadius={sizing.knobBorderRadius}
      />
    </Box>
  );

  return (
    <Box
      testID={testID}
      role="switch"
      aria-checked={checked}
      aria-disabled={isDisable || undefined}
      aria-invalid={isError || undefined}
      aria-describedby={ariaDescribedBy}
      aria-label={
        ariaLabelledBy ? undefined : ariaLabel || (label ? undefined : "Switch")
      }
      aria-labelledby={ariaLabelledBy}
      tabIndex={0}
      flexDirection="row"
      alignItems={description || showErrorLabel ? "flex-start" : "center"}
      gap={sizing.labelGap}
      onPress={handlePress}
      onKeyDown={handleKeyDown}
      cursor={!isDisable ? "pointer" : "default"}
      focusStyle={{
        outlineColor: theme.colors.border.brand,
        outlineWidth: 2,
        outlineOffset: 2,
        outlineStyle: "solid",
      }}
    >
      {labelPosition === "left" && labelBlock}
      {toggle}
      {labelPosition === "right" && labelBlock}
    </Box>
  );
};
